import availableModuleSettings from '@/config/available_module_settings.json'

export const HAZCAMS_SETUP_STEPS = ['video-tiles', 'weather-tiles', 'map', 'camera-controls', 'dashboard']

const clone = (value) => JSON.parse(JSON.stringify(value))

function mergeObjects(base, override) {
  if (!override || typeof override !== 'object' || Array.isArray(override)) {
    return clone(base)
  }

  return Object.entries(base).reduce((result, [key, value]) => {
    const incoming = override[key]
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      incoming &&
      typeof incoming === 'object' &&
      !Array.isArray(incoming)
    ) {
      result[key] = mergeObjects(value, incoming)
    } else if (incoming !== undefined) {
      result[key] = clone(incoming)
    } else {
      result[key] = clone(value)
    }
    return result
  }, {})
}

export function createDefaultPreferences({ completeSetup = false } = {}) {
  const defaults = clone(availableModuleSettings)

  if (completeSetup) {
    const setup = defaults.hazcams.setup
    setup.completed = true
    setup.completedAt = new Date().toISOString()
    HAZCAMS_SETUP_STEPS.forEach((stepId) => {
      setup.steps[stepId] = true
    })
  }

  return defaults
}

export function isStructuredPreferences(value) {
  return Boolean(value?.hazcams?.user && typeof value.hazcams.user === 'object')
}

export function normalizeStructuredPreferences(value) {
  const defaults = createDefaultPreferences()
  if (!isStructuredPreferences(value)) return defaults

  const normalized = {
    ...defaults,
    ...value,
    schemaVersion: availableModuleSettings.schemaVersion,
    user: mergeObjects(defaults.user, value.user),
    hazcams: {
      ...defaults.hazcams,
      ...value.hazcams,
      setup: mergeObjects(defaults.hazcams.setup, value.hazcams.setup),
      user: mergeObjects(defaults.hazcams.user, value.hazcams.user),
    },
    'strike-zone': {
      ...defaults['strike-zone'],
      ...(value['strike-zone'] || {}),
      setup: mergeObjects(defaults['strike-zone'].setup, value['strike-zone']?.setup),
      user: mergeObjects(defaults['strike-zone'].user, value['strike-zone']?.user),
    },
    hazalerts: {
      ...defaults.hazalerts,
      ...(value.hazalerts || {}),
      setup: mergeObjects(defaults.hazalerts.setup, value.hazalerts?.setup),
      user: mergeObjects(defaults.hazalerts.user, value.hazalerts?.user),
    },
  }

  const metrics = normalized.hazcams.user['weather-tiles'].weatherVisibleMetrics
  if (Array.isArray(metrics) && metrics.includes('significant_wave_height')) {
    normalized.hazcams.user['weather-tiles'].weatherVisibleMetrics = [
      ...metrics.filter((metric) => metric !== 'significant_wave_height'),
      'significant_wave_height',
    ]
  }

  const previousModuleNotifications = value.hazcams?.user?.notifications
  if (!value.user?.notifications && previousModuleNotifications) {
    normalized.user.notifications = mergeObjects(
      defaults.user.notifications,
      previousModuleNotifications
    )
  }

  // Numeric page sizes from schema v13 and earlier now map to the responsive
  // fill mode. Preserve the existing all-pages behavior for users who chose it.
  const stationPreferences = normalized.hazcams.user.stations
  stationPreferences.camerasPerPage = stationPreferences.camerasPerPage === 'all' ? 'all' : 'fill'
  stationPreferences.tableItemsPerPage = stationPreferences.tableItemsPerPage === 'all' ? 'all' : 'fill'

  return normalized
}

export function getHazcamsUserPreferences(value) {
  return normalizeStructuredPreferences(value).hazcams.user
}

export function flattenHazcamsPreferences(value) {
  const normalized = normalizeStructuredPreferences(value)
  const globalPreferences = Object.values(normalized.user).reduce(
    (flat, settings) => ({ ...flat, ...(settings || {}) }),
    {}
  )
  const userPreferences = normalized.hazcams.user

  return Object.entries(userPreferences).reduce((flat, [moduleId, settings]) => {
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
      return flat
    }
    return { ...flat, ...settings }
  }, globalPreferences)
}

export function findPreferenceModule(key) {
  const globalUserPreferences = availableModuleSettings.user
  const globalModuleId = Object.keys(globalUserPreferences).find(
    (moduleId) => Object.hasOwn(globalUserPreferences[moduleId], key)
  )
  if (globalModuleId) return { scope: 'user', moduleId: globalModuleId }

  const userPreferences = availableModuleSettings.hazcams.user
  const moduleId = Object.keys(userPreferences).find(
    (moduleId) => Object.hasOwn(userPreferences[moduleId], key)
  )
  return moduleId ? { scope: 'hazcams', moduleId } : null
}

export function setPreferenceAtLocation(value, location, key, settingValue) {
  const next = normalizeStructuredPreferences(value)
  if (location.scope === 'user') {
    next.user[location.moduleId] = {
      ...next.user[location.moduleId],
      [key]: settingValue,
    }
    return next
  }

  return setModulePreference(next, location.moduleId, key, settingValue)
}

export function setModulePreference(value, moduleId, key, settingValue) {
  const next = normalizeStructuredPreferences(value)
  next.hazcams.user[moduleId] = {
    ...next.hazcams.user[moduleId],
    [key]: settingValue,
  }
  return next
}

export function completeSetup(value) {
  const next = normalizeStructuredPreferences(value)
  HAZCAMS_SETUP_STEPS.forEach((stepId) => {
    next.hazcams.setup.steps[stepId] = true
  })
  next.hazcams.setup.completed = true
  next.hazcams.setup.completedAt = new Date().toISOString()
  return next
}
